/**
 * <bundle-offer> — tek bir "2 al 1 öde" paketi.
 *
 * Sayfada birden çok örneği bulunur: section, metafield'daki ürün listesini
 * sırayla ikişerli (data-group-size) gruplara böler ve her grup için bir
 * <bundle-offer> basar. Her paket kendi içinde bağımsız çalışır.
 *
 * İçindeki TÜM kartlar pakete dahildir; seçim yoktur. Müşteri sadece her
 * ürünün varyantını belirler ve tek butonla ikisini birden sepete atar.
 *
 * Gösterilen fiyat, paketteki varyantların EN PAHALI `data-paid-count`
 * tanesinin toplamıdır. 2 al 1 öde için bu, pahalı olanın fiyatı demektir.
 *
 * ÖNEMLİ: Bu bileşen sadece arayüzdür. Ödeme sırasında ucuz ürünün bedava
 * olmasını Shopify tarafında bir OTOMATİK İNDİRİM sağlar (Buy X get Y).
 * Tema kasadaki fiyatı değiştiremez. Kurulum için
 * sections/bundle-2-al-1-ode.liquid dosyasının başındaki nota bakın.
 */
if (!customElements.get("bundle-offer")) {
  class BundleOffer extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll("[data-bundle-card]")];
      this.submitButton = this.querySelector("[data-bundle-submit]");
      this.priceTarget = this.querySelector("[data-bundle-price]");
      this.compareTarget = this.querySelector("[data-bundle-compare]");
      // Ustu cizili tutar <s> icinde bir deger span'inda duruyor (yaninda
      // ekran okuyucular icin gizli bir etiket var), gizleme <s> uzerinde.
      this.compareWrapper = this.querySelector("[data-bundle-compare-wrapper]");
      this.errorTarget = this.querySelector("[data-bundle-error]");

      this.moneyFormat = this.dataset.moneyFormat || "{{amount}}";

      // Kaç ürünün parası ödenecek. Paket boyutundan küçük olmak zorunda.
      this.paidCount = Math.min(
        Math.max(1, this.cards.length - 1),
        Math.max(1, Number(this.dataset.paidCount) || 1)
      );

      if (this.cards.length < 2 || !this.submitButton) return;

      this.items = this.cards.map((card) => this.readCard(card));

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.addEventListener("change", this.onOptionChange);
      this.submitButton.addEventListener("click", this.onSubmit);

      this.items.forEach((_, index) => this.syncCard(index));
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onOptionChange);
      this.submitButton?.removeEventListener("click", this.onSubmit);
    }

    readCard(card) {
      const script = card.querySelector("[data-bundle-variants]");
      let variants = [];

      try {
        variants = JSON.parse(script ? script.textContent : "[]");
      } catch (error) {
        console.error("Bundle offer: varyant verisi okunamadı.", error);
      }

      return {
        card,
        variants,
        optionCount: Number(card.dataset.optionCount || 0),
        variant: null
      };
    }

    /** Bir kartta o an işaretli olan seçenek değerlerini sırayla döndürür. */
    selectedOptions(index) {
      const { card, optionCount } = this.items[index];

      return Array.from({ length: optionCount }, (_, position) => {
        const input = card.querySelector(
          `input[type="radio"][data-option-position="${position + 1}"]:checked`
        );
        return input ? input.value : null;
      });
    }

    /**
     * Seçili değerlere karşılık gelen varyantı bulur, ardından her seçenek
     * değerinin (diğer seçimler sabitken) ulaşılabilir/satın alınabilir
     * olup olmadığına göre inputları işaretler.
     */
    syncCard(index) {
      const item = this.items[index];
      const selected = this.selectedOptions(index);

      item.variant =
        item.variants.find((variant) =>
          selected.every(
            (value, position) => value === null || variant.options[position] === value
          )
        ) || null;

      item.card.querySelectorAll('input[type="radio"][data-option-position]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;

        const matches = (variant, requireAvailable) => {
          if (variant.options[position] !== input.value) return false;
          if (requireAvailable && !variant.available) return false;
          return selected.every(
            (value, other) =>
              other === position || value === null || variant.options[other] === value
          );
        };

        input.disabled = !item.variants.some((variant) => matches(variant, false));
        input
          .closest("[data-option-value]")
          ?.classList.toggle("is-unavailable", !item.variants.some((v) => matches(v, true)));
      });

      this.updateCardMedia(index);
    }

    /** Varyantın kendi görseli varsa kart görselini onunla değiştirir. */
    updateCardMedia(index) {
      const { card, variant } = this.items[index];
      const image = card.querySelector("[data-bundle-image]");
      const source = variant?.featured_image?.src;

      if (!image || !source) return;

      // Shopify varyant görselini tam boyutta veriyor; kart genişliğine indiriyoruz.
      const resized = source.replace(/(\.[a-z]+)(\?|$)/i, "_480x$1$2");

      if (image.getAttribute("src") !== resized) {
        image.setAttribute("src", resized);
        image.removeAttribute("srcset");
      }
    }

    onOptionChange(event) {
      if (!event.target.matches('input[type="radio"][data-option-position]')) return;

      const index = this.cards.indexOf(event.target.closest("[data-bundle-card]"));
      if (index < 0) return;

      this.syncCard(index);
      this.render();
    }

    /** Paket fiyatı: varyantların en pahalı `paidCount` tanesinin toplamı. */
    render() {
      const variants = this.items.map((item) => item.variant);
      const allChosen = variants.every(Boolean);
      const allAvailable = allChosen && variants.every((variant) => variant.available);

      if (allChosen) {
        const prices = variants.map((variant) => variant.price).sort((a, b) => b - a);
        const payable = prices.slice(0, this.paidCount).reduce((sum, price) => sum + price, 0);
        const total = prices.reduce((sum, price) => sum + price, 0);

        // innerHTML, textContent degil: magazanin para birimi formati HTML
        // icerebiliyor (ornegin <span class='money'>{{amount}}TL</span>) ve
        // textContent bunu etiketiyle birlikte duz metin olarak basiyordu.
        // Temanin kendi fiyat guncellemesi de ayni sekilde innerHTML kullaniyor.
        // Enjekte edilen tek HTML magaza ayarindaki format; tutar sayidan uretiliyor.
        this.priceTarget.innerHTML = this.money(payable);

        if (this.compareTarget && this.compareWrapper) {
          this.compareTarget.innerHTML = this.money(total);
          // Tek urunun parasi odeniyorsa toplam her zaman buyuktur; yine de
          // esitlik durumunda (or. ucuz urun 0 TL) ustu cizili tutari gizle.
          this.compareWrapper.hidden = total <= payable;
        }
      } else {
        this.priceTarget.textContent = "";
        if (this.compareWrapper) this.compareWrapper.hidden = true;
      }

      this.submitButton.disabled = !allAvailable;
      this.submitButton.textContent =
        allChosen && !allAvailable
          ? window.variantStrings?.soldOut || "Tükendi"
          : this.submitButton.dataset.label;

      this.showError("");
    }

    money(amount) {
      return typeof formatPrice === "function"
        ? formatPrice(amount, this.moneyFormat)
        : (amount / 100).toFixed(2);
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = !message;
    }

    async onSubmit(event) {
      event.preventDefault();

      const variants = this.items.map((item) => item.variant);
      if (!variants.every((variant) => variant?.available)) return;

      this.submitButton.setAttribute("disabled", "");
      this.submitButton.classList.add("add-to-cart--is-disabled");

      const cart =
        document.querySelector("cart-notification") || document.querySelector("cart-drawer");

      // Aynı varyant pakette iki kez yer alabilir (ör. aynı ürün iki kez
      // listelenmişse); Shopify tek satırda beklediği için birleştiriyoruz.
      const quantities = new Map();
      variants.forEach((variant) => {
        quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
      });

      const payload = {
        items: [...quantities].map(([id, quantity]) => ({ id, quantity }))
      };

      if (cart) {
        payload.sections = cart.getSectionsToRender().map((section) => section.section);
        payload.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        // Sepete ekleme hataları 4xx ile döner ve gövdede "description" taşır.
        if (!response.ok || result.status) {
          this.showError(result.description || result.message || window.cartStrings?.error || "");
          return;
        }

        if (cart) cart.renderContents(result);
        if (typeof updateCartCounters === "function") updateCartCounters();
      } catch (error) {
        console.error("Bundle offer: sepete eklenemedi.", error);
        this.showError(window.cartStrings?.error || "");
      } finally {
        this.submitButton.removeAttribute("disabled");
        this.submitButton.classList.remove("add-to-cart--is-disabled");
        this.render();
      }
    }
  }

  customElements.define("bundle-offer", BundleOffer);
}
